import {
  ApiError,
  BadRequestError,
  InternalServerError,
} from "./errors.classes";

/**
 * This extends the error class to add a toJSON function. It ensures that
 * the error object is serializable, which is important for logging.
 * https://stackoverflow.com/a/18391400/8292279
 */
if (!("toJSON" in Error.prototype)) {
  Object.defineProperty(Error.prototype, "toJSON", {
    value() {
      const alt: Record<string, unknown> = {};
      // oxlint-disable-next-line typescript/no-explicit-any
      Object.getOwnPropertyNames(this).forEach(function (this: any, key) {
        alt[key] = this[key];
      }, this);
      return alt;
    },
    configurable: true,
    writable: true,
  });
}

/**
 * Standard node-postgres `DatabaseError` diagnostic fields. These carry the
 * SQLSTATE `code` (e.g. `55P03` lock_not_available, `57014` statement_timeout,
 * `53300` too_many_connections, `08*` connection failures, `23505`
 * unique_violation) plus the human-readable detail/hint/constraint context.
 * They are the difference between a useless `Failed query: <sql>` log line and
 * an actionable root cause, so they must NEVER be swallowed.
 *
 * The pg driver sets these on its `DatabaseError`, which Drizzle nests as a
 * `.cause` — frequently two levels deep once our own error wrapping is applied
 * (e.g. `InternalServerError` -> `DrizzleQueryError` -> pg `DatabaseError`). The
 * previous one-level-deep `serializeError` therefore dropped them entirely,
 * which is why a real production DB incident logged only the SQL text with no
 * SQLSTATE code.
 */
const PG_DIAGNOSTIC_FIELDS = [
  "code",
  "detail",
  "hint",
  "severity",
  "routine",
  "constraint",
  "table",
  "schema",
  "column",
  "dataType",
  "where",
  "position",
  "internalPosition",
  "internalQuery",
  "file",
  "line",
] as const;

/**
 * Bound the cause walk so a pathological or self-referential chain can never
 * stall the logger. Real driver/ORM wrapping is only 2-3 levels deep.
 */
const MAX_CAUSE_DEPTH = 8;

const serializeErrorNode = (
  err: unknown,
  depth: number,
  seen: WeakSet<object>,
): unknown => {
  if (!(err instanceof Error)) {
    if (err === undefined) return undefined;
    if (err === null || typeof err !== "object") {
      return { value: err, valueType: typeof err };
    }
    // A non-Error object cause (rare) — shallow-capture without recursing.
    return { ...(err as Record<string, unknown>) };
  }

  if (seen.has(err) || depth > MAX_CAUSE_DEPTH) {
    return { name: err.name, message: err.message, truncated: true };
  }
  seen.add(err);

  const node: Record<string, unknown> = {
    name: err.name,
    type: (err as ApiError).type,
    message: err.message,
    stack: err.stack,
    fields: (err as ApiError).fields,
    // Enumerable own properties, EXCLUDING `cause` — that is handled by the
    // recursion below so it is always deterministically serialised rather than
    // left as a raw Error instance (which the old trailing spread did, silently
    // clobbering the curated one-level cause).
    ...Object.fromEntries(
      Object.entries(err).filter(([key]) => key !== "cause"),
    ),
  };

  // Explicitly surface Postgres driver diagnostics even when they are
  // non-enumerable, so a query failure's SQLSTATE code is never lost.
  for (const key of PG_DIAGNOSTIC_FIELDS) {
    const value = (err as unknown as Record<string, unknown>)[key];
    if (value !== undefined) node[key] = value;
  }

  // Walk the ENTIRE `.cause` chain. Read `.cause` directly rather than relying
  // on `Object.entries` to surface it — it is frequently a non-enumerable Error
  // option and would otherwise be missed. This is the same direct-`.cause`
  // traversal the tRPC unique-violation mapper uses to read SQLSTATE.
  if (err.cause !== undefined) {
    node.cause = serializeErrorNode(err.cause, depth + 1, seen);
  }

  return node;
};

/*
 * Serialise an Error (or unknown) into a plain JSON-serialisable object.
 * – Includes name, type, message, stack, fields, plus any enumerable custom
 *   properties.
 * – Walks the ENTIRE `.cause` chain (bounded depth, cycle-safe) and explicitly
 *   captures Postgres driver diagnostics (SQLSTATE `code`, detail, hint, …), so
 *   the pg root cause behind a Drizzle `Failed query: <sql>` is never swallowed.
 */
export const serializeError = (err: Error): Record<string, unknown> =>
  serializeErrorNode(err, 0, new WeakSet()) as Record<string, unknown>;

export const apiErrorFormatter = (err: Error) => {
  // This is the most reliable way to detect a body-parser error (https://github.com/expressjs/body-parser?tab=readme-ov-file#entity-parse-failed)
  const isBodyParserError = "type" in err && err.type === "entity.parse.failed";
  if (err instanceof SyntaxError && isBodyParserError) {
    const formattedError = new BadRequestError("Invalid JSON payload", {
      type: "invalidJsonPayload",
      cause: err,
    });

    return formattedError;
  }

  if (err.message === "request entity too large") {
    const formattedError = new BadRequestError("Request payload is too large", {
      type: "requestPayloadTooLarge",
      cause: err,
    });

    return formattedError;
  }

  if (err instanceof ApiError) {
    return err;
  }

  // Unrecognized errors are internal faults whose messages can carry secrets
  // (a DrizzleQueryError's message embeds the full SQL text and bound params,
  // including API-key hashes). Clients get the generic message; the original
  // error rides along as `cause` for serializeError / span recording.
  const defaultError = new InternalServerError(undefined, {
    cause: err,
  });

  return defaultError;
};
